import * as T from 'three';
import type { WorldData, Progress, SimState, Vec3 } from './types';
import { distance } from './types';
export const MODES = [
    { id: 'explore', icon: '✧', name: 'Just explore', text: 'Follow your curiosity. Every branch is a beginning.' },
    { id: 'trial', icon: '◷', name: 'Canopy Run', text: 'Cross the First Ravine. Chase a medal and your own ghost.' },
    { id: 'race', icon: '⚑', name: 'Crown to Crown', text: 'Race the troop through the village to Cloud Crown.' },
    { id: 'tag', icon: '☀', name: 'Monkey Tag', text: 'Catch three monkeys. They know every shortcut.' },
    { id: 'bandits', icon: '◒', name: 'Banana Bandits', text: 'Steal three mangoes from the village nest. Bring them home.' },
    { id: 'golden', icon: '★', name: 'Golden Banana', text: 'Find the golden fruit and hold it for thirty seconds.' },
    { id: 'hunt', icon: '⌕', name: 'Canopy Hunt', text: 'Find the orchid, temple idol and waterfall shell.' },
    { id: 'follow', icon: '↝', name: 'Follow Me', text: 'Follow Pip across the safe line. Take your time.' },
    { id: 'memory', icon: '♫', name: 'Monkey See, Monkey Do', text: 'Watch the calls, then echo the troop’s sequence.' },
    { id: 'rescue', icon: '♡', name: 'Monkey Rescue', text: 'Find Sprout at Three Sisters. Carry the baby home.' },
    { id: 'relay', icon: '↗', name: 'Coconut Relay', text: 'Carry a coconut to the village and pass it to Rio.' },
    { id: 'storm', icon: '☂', name: 'Rising Waters', text: 'Follow the high route to Cloud Crown before the water rises.' },
];
export class Activities {
    followPoints: Vec3[] = [];
    mode = 'explore';
    private authoredActivity?:string;
    get authoredId(){return this.authoredActivity;}
    get recordKey(){return `${this.authoredActivity?`authored:${this.authoredActivity}`:this.mode}:${this.progress.settings.assist}:v${this.world.physicsVersion}`;}
    private get race(){return this.world.activities.find(a=>a.id===this.authoredActivity)||this.world.activities[this.mode==='race'?1:0];}
    elapsed = 0;
    index = 0;
    score = 0;
    carrying = false;
    complete = false;
    started = false;
    target: Vec3 | null = null;
    message = '';
    trace: number[][] = [];
    tagged = new Set<number>();
    markers = new T.Group();
    ghost?: T.Object3D;
    private recordClock = 0;
    private holdTime = 0;
    private memoryRound = 0;
    private memoryInput: number[] = [];
    memorySequence = [1, 3, 2, 1, 2];
    memoryWatch = 0;
    private interactCooldown = 0;
    constructor(public world: WorldData, private progress: Progress, private scene: T.Scene, private notify: (text: string, kind?: string) => void, private finish: (name: string, time: number, medal: string) => void) { scene.add(this.markers); }
    start(mode: string, activityId?:string) {
        this.authoredActivity=activityId;
        this.mode = mode;
        this.elapsed = 0;
        this.index = 0;
        this.score = 0;
        this.carrying = false;
        this.complete = false;
        this.started = true;
        this.trace = [];
        this.tagged.clear();
        this.holdTime = 0;
        this.interactCooldown = 0;
        this.recordClock = 0;
        this.memoryRound = 0;
        this.memoryInput = [];
        this.memoryWatch = mode === 'memory' ? 4 : 0;
        this.clearMarkers();
        this.target = null;
        const route = this.race;
        if (mode === 'trial' || mode === 'race') {
            route.gates.forEach((p, i) => { const ring = new T.Mesh(new T.TorusGeometry(route.radius * .72, .07, 6, 48), new T.MeshBasicMaterial({ color: i === 0 ? '#f8d76b' : '#e5eee0', transparent: true, opacity: i === 0 ? .85 : .27 })); ring.position.set(...p); this.markers.add(ring); });
        }
        if (mode !== 'explore') this.notify(MODES.find(m => m.id === mode)?.name || 'Explore');
        this.updateTarget();
    }
    private clearMarkers() {
        for (const o of [...this.markers.children]) {
            this.markers.remove(o);
            if (o instanceof T.Mesh) {
                o.geometry.dispose();
                (o.material as T.Material).dispose();
            }
        }
    }
    private updateTarget() {
        const targets: Record<string, Vec3> = { bandits: this.carrying ? [0, 15, 0] : [-32, 19, 32], golden: [0, 15, 27], rescue: this.carrying ? [0, 15, 0] : [0, 15, 27], relay: this.carrying ? [-32, 19, 32] : [0, 15, 0], storm: [-6, 36, 130] };
        if (this.mode === 'trial' || this.mode === 'race')
            this.target = this.race.gates[this.index] || null;
        else if (this.mode === 'hunt')
            this.target = ([[0, 15, 27], [38, 13, 42], [-48, 21, 87]] as Vec3[])[this.index] || null;
        else if (this.mode === 'follow')
            this.target = (this.followPoints.length ? this.followPoints : this.world.routes.find(r => r.id === 'safe')!.points)[this.index] || null;
        else
            this.target = targets[this.mode] || null;
    }
    update(dt: number, s: SimState, npcs: SimState[]) {
        if (!this.started || this.complete)
            return;
        this.elapsed += dt;
        this.interactCooldown = Math.max(0, this.interactCooldown - dt);
        this.recordClock += dt;
        if (this.recordClock > .10) {
            this.recordClock = 0;
            if (this.trace.length < 18000)
                this.trace.push([this.elapsed, ...s.p, ...s.v, s.mode, s.grab]);
        }
        if (this.mode === 'trial' || this.mode === 'race') {
            const route = this.race;
            if (this.target && distance(s.p, this.target) < route.radius) {
                this.index++;
                if (this.index >= route.gates.length) {
                    const medal = this.elapsed <= route.medals[0] ? 'Gold' : this.elapsed <= route.medals[1] ? 'Silver' : 'Bronze';
                    this.win(medal);
                }
                else {
                    this.notify(`Gate ${this.index} / ${route.gates.length}`, 'fruit');
                    this.markers.children.forEach((o, i) => { const m = (o as T.Mesh).material as T.MeshBasicMaterial; m.opacity = i === this.index ? .9 : i < this.index ? .08 : .25; m.color.set(i === this.index ? '#f8d76b' : '#e5eee0'); });
                    this.updateTarget();
                }
            }
        }
        if (this.mode === 'tag') {
            npcs.slice(0, 4).forEach((n, i) => {
                if (this.complete)
                    return;
                if (distance(s.p, n.p) < 1.8 && !this.tagged.has(i)) {
                    this.tagged.add(i);
                    this.score++;
                    this.notify(`Got ${this.world.npcs[i].name}! ${this.score} / 3`, 'grab');
                    if (this.score >= 3)
                        this.win('Troop champion');
                }
            });
        }
        if (this.mode === 'golden' && this.carrying) {
            this.holdTime += dt;
            if (npcs.some(n => distance(n.p, s.p) < 1.2) && this.interactCooldown === 0) {
                this.carrying = false;
                this.holdTime = 0;
                this.interactCooldown = 3;
                this.notify('The golden banana slipped away. Catch it again!');
            }
            if (this.holdTime >= 30)
                this.win('Golden keeper');
        }
        if (this.mode === 'follow' && this.target && distance(s.p, this.target) < 3) {
            this.index++;
            this.notify('Lovely catch. On to the next branch!', 'grab');
            this.updateTarget();
            if (!this.target)
                this.win('A new friend');
        }
        if (this.mode === 'storm') {
            if (s.p[1] < this.waterLevel) {
                this.notify('The water caught up. Try the high branches!');
                this.started = false;
            }
            if (this.target && distance(s.p, this.target) < 7)
                this.win('Above the clouds');
        }
        if (this.memoryWatch > 0)
            this.memoryWatch = Math.max(0, this.memoryWatch - dt);
    }
    get waterLevel() { return this.mode === 'storm' ? Math.max(0, (this.elapsed - 10) * .14) : 0; }
    interact(s: SimState) {
        if (this.complete || this.interactCooldown > 0)
            return;
        this.interactCooldown = .4;
        if (this.target && distance(s.p, this.target) > 5) {
            this.notify('Get a little closer to the marker.');
            return;
        }
        if (this.mode === 'bandits') {
            if (this.carrying) {
                this.score++;
                this.carrying = false;
                this.notify(`Mango delivered · ${this.score} / 3`, 'fruit');
                if (this.score === 3)
                    this.win('Master bandit');
            }
            else {
                this.carrying = true;
                this.notify('Mango in hand. Take it home!', 'fruit');
            }
            this.updateTarget();
        }
        if (this.mode === 'golden') {
            this.carrying = true;
            this.holdTime = 0;
            this.interactCooldown = 3;
            this.notify('Keep moving! Hold the golden banana for 30 seconds.', 'fruit');
        }
        if (this.mode === 'rescue') {
            if (this.carrying)
                this.win('Home, safe and sound');
            else {
                this.carrying = true;
                this.notify('Sprout is holding on. Bring the little one home.', 'fruit');
            }
            this.updateTarget();
        }
        if (this.mode === 'relay') {
            if (this.carrying)
                this.win('Perfect pass');
            else {
                this.carrying = true;
                this.notify('Take the coconut to Rio in the village.', 'fruit');
            }
            this.updateTarget();
        }
        if (this.mode === 'hunt') {
            this.index++;
            this.notify(['A wild orchid!', 'A river shell!', 'The sun idol!'][this.index - 1], 'discover');
            this.updateTarget();
            if (this.index === 3)
                this.win('Keen eyes');
        }
    }
    call(n: number) {
        if (this.mode !== 'memory' || this.complete || this.memoryWatch > 0)
            return;
        this.memoryInput.push(n);
        if (n !== this.memorySequence[this.memoryInput.length - 1]) {
            this.memoryInput = [];
            this.memoryWatch = 4;
            this.notify('Listen once more. You have this!');
            return;
        }
        if (this.memoryInput.length === this.memoryRound + 2) {
            this.memoryRound++;
            this.memoryInput = [];
            if (this.memoryRound >= 4)
                this.win('One of the troop');
            else {
                this.memoryWatch = 4;
                this.notify('That’s it! A little longer this time.', 'fruit');
            }
        }
    }
    drop(s: SimState) {
        if (this.carrying) {
            this.carrying = false;
            this.target = [...s.p];
            this.notify('Set down safely. Press E nearby to pick up.');
        }
    }
    private win(medal: string) {
        this.complete = true;
        this.carrying = false;
        const key = this.recordKey;
        if (!this.progress.bests[key] || this.elapsed < this.progress.bests[key]) {
            this.progress.bests[key] = this.elapsed;
            this.progress.ghosts[key] = this.trace;
        }
        const completion=this.authoredActivity?`authored:${this.authoredActivity}`:this.mode;
        if (!this.progress.completed.includes(completion))
            this.progress.completed.push(completion);
        this.finish(this.title, this.elapsed, medal);
    }
    get title() { return (this.authoredActivity?this.race.name:undefined)||MODES.find(m => m.id === this.mode)?.name || 'Explore'; }
    get objective() {
        if (this.complete)
            return 'Nicely done. Another adventure is waiting.';
        switch (this.mode) {
            case 'explore': return 'Follow the fruit across the First Ravine.';
            case 'trial':
            case 'race': return `Gate ${this.index + 1} of ${this.race.gates.length} · ${this.elapsed.toFixed(1)}s`;
            case 'tag': return `Catch the troop · ${this.score} / 3 tagged`;
            case 'bandits': return this.carrying ? `Bring the mango home · ${this.score} / 3` : `Find a mango at Market Crown · ${this.score} / 3`;
            case 'golden': return this.carrying ? `Hold on! ${Math.floor(this.holdTime)} / 30 seconds` : 'Find the golden banana at Three Sisters';
            case 'hunt': return `Find the ${['orchid', 'shell', 'idol'][this.index] || 'treasures'} · ${this.index} / 3`;
            case 'follow': return `Follow Pip’s safe line · ${this.index} / ${this.followPoints.length || 7} stops`;
            case 'memory': return this.memoryWatch > 0 ? `Listen: ${this.memorySequence.slice(0, this.memoryRound + 2).join(' · ')}` : `Your turn · press 1, 2 or 3 (${this.memoryInput.length} / ${this.memoryRound + 2})`;
            case 'rescue': return this.carrying ? 'Bring Sprout back to Home Tree' : 'Find Sprout at Three Sisters · E to help';
            case 'relay': return this.carrying ? 'Carry the coconut to Rio at Market Crown' : 'Pick up the coconut at Home Tree · E';
            case 'storm': return `Reach Eagle’s Pinnacle · water ${this.waterLevel.toFixed(1)} m`;
            default: return '';
        }
    }
}
export function sampleGhost(tape: number[][], time: number) {
    if (!tape.length)
        return null;
    let low = 0, high = tape.length - 1;
    while (low < high) {
        const mid = Math.floor((low + high + 1) / 2);
        if (tape[mid][0] <= time)
            low = mid;
        else
            high = mid - 1;
    }
    const a = tape[low], b = tape[Math.min(low + 1, tape.length - 1)], t = Math.max(0, Math.min(1, (time - a[0]) / Math.max(.0001, b[0] - a[0])));
    return { p: [1, 2, 3].map(i => a[i] + (b[i] - a[i]) * t) as Vec3, v: [4, 5, 6].map(i => a[i] + (b[i] - a[i]) * t) as Vec3, mode: a[7] };
}
